// Email service using Resend API

export interface EmailTemplate {
  to: string;
  subject: string;
  html: string;
  from?: string;
}

export interface FeedbackRequestEmail {
  recipientName: string;
  recipientEmail: string;
  requesterName: string;
  feedbackLink: string;
  message?: string;
}

export async function sendFeedbackRequestEmail(data: FeedbackRequestEmail): Promise<boolean> {
  try {
    const apiKey = import.meta.env.VITE_RESEND_API_KEY;
    
    if (!apiKey) {
      console.warn('Resend API key not configured, email not sent');
      return false;
    }

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Feedback Request from ${data.requesterName}</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f3f4f6;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f3f4f6; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" border="0" style="background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          <tr>
            <td style="background: linear-gradient(135deg, #8CAFA8 0%, #7A9B94 100%); padding: 40px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: 700;">Feedback Request</h1>
              <p style="color: #E0EBE9; margin: 10px 0 0 0; font-size: 16px;">from ${data.requesterName}</p>
            </td>
          </tr>
          <tr>
            <td style="padding: 40px;">
              <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                Hi ${data.recipientName || 'there'},
              </p>
              <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                ${data.requesterName} has requested your feedback to help with professional development. Your insights and honest perspective would be greatly appreciated.
              </p>
              ${data.message ? `
              <div style="background-color: #F0F4F3; border-left: 4px solid #8CAFA8; padding: 20px; margin: 20px 0; border-radius: 4px;">
                <p style="color: #7A9B94; font-size: 15px; line-height: 1.6; margin: 0; font-style: italic;">
                  "${data.message}"
                </p>
              </div>
              ` : ''}
              <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 20px 0;">
                You can provide feedback in two ways:
              </p>
              <ul style="color: #4b5563; font-size: 15px; line-height: 1.8; margin: 0 0 30px 20px;">
                <li>Record a 3-5 minute voice message (recommended)</li>
                <li>Type your thoughts directly</li>
              </ul>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  <td align="center" style="padding: 20px 0;">
                    <a href="${data.feedbackLink}" style="display: inline-block; background-color: #8CAFA8; color: #ffffff; text-decoration: none; padding: 16px 40px; border-radius: 8px; font-weight: 600; font-size: 16px; box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);">
                      Share Your Feedback
                    </a>
                  </td>
                </tr>
              </table>
              <p style="color: #6b7280; font-size: 14px; line-height: 1.6; margin: 20px 0 0 0; text-align: center;">
                This link will expire in 30 days
              </p>
            </td>
          </tr>
          <tr>
            <td style="background-color: #f9fafb; padding: 30px; text-align: center; border-top: 1px solid #e5e7eb;">
              <p style="color: #6b7280; font-size: 14px; margin: 0 0 10px 0;">
                Powered by <strong style="color: #8CAFA8;">Mentra</strong>
              </p>
              <p style="color: #9ca3af; font-size: 12px; margin: 0;">
                AI-powered career feedback platform
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `;

    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        from: 'Mentra <feedback@getmentra.com>',
        to: [data.recipientEmail],
        subject: `${data.requesterName} has requested your feedback`,
        html: html
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Email sending failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
      throw new Error(`Email sending failed: ${response.statusText} - ${JSON.stringify(errorData)}`);
    }

    const result = await response.json();
    console.log('Email sent successfully:', result);
    return true;
  } catch (error) {
    console.error('Error sending email:', error);
    return false;
  }
}

export async function sendReminderEmail(data: FeedbackRequestEmail): Promise<boolean> {
  try {
    const apiKey = import.meta.env.VITE_RESEND_API_KEY;
    
    if (!apiKey) {
      console.warn('Resend API key not configured, reminder not sent');
      return false;
    }

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Reminder: Feedback Request</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f3f4f6;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f3f4f6; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" border="0" style="background-color: #ffffff; border-radius: 12px; overflow: hidden;">
          <tr>
            <td style="padding: 40px;">
              <h2 style="color: #1f2937; margin: 0 0 20px 0; font-size: 24px;">Friendly Reminder</h2>
              <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                Hi ${data.recipientName || 'there'},
              </p>
              <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                ${data.requesterName} is still hoping to receive your feedback. If you have a few minutes, your insights would be incredibly valuable for their professional growth.
              </p>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  <td align="center" style="padding: 20px 0;">
                    <a href="${data.feedbackLink}" style="display: inline-block; background-color: #8CAFA8; color: #ffffff; text-decoration: none; padding: 16px 40px; border-radius: 8px; font-weight: 600; font-size: 16px;">
                      Share Your Feedback
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `;

    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        from: 'Mentra <feedback@getmentra.com>',
        to: [data.recipientEmail],
        subject: `Reminder: ${data.requesterName} is waiting for your feedback`,
        html: html
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Reminder email failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
    } else {
      const result = await response.json();
      console.log('Reminder email sent successfully:', result);
    }

    return response.ok;
  } catch (error) {
    console.error('Error sending reminder:', error);
    return false;
  }
}

export async function sendConfirmationEmail(recipientEmail: string, requesterName: string): Promise<boolean> {
  try {
    const apiKey = import.meta.env.VITE_RESEND_API_KEY;

    if (!apiKey) {
      return false;
    }

    const html = `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: sans-serif;">
  <div style="max-width: 600px; margin: 0 auto; padding: 40px;">
    <h2 style="color: #10b981;">Thank You!</h2>
    <p>Your feedback has been successfully submitted to ${requesterName}.</p>
    <p>Your insights will help with their professional development.</p>
  </div>
</body>
</html>
    `;

    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        from: 'Mentra <feedback@getmentra.com>',
        to: [recipientEmail],
        subject: 'Feedback submitted successfully',
        html: html
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Confirmation email failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
    } else {
      const result = await response.json();
      console.log('Confirmation email sent successfully:', result);
    }

    return response.ok;
  } catch (error) {
    console.error('Error sending confirmation:', error);
    return false;
  }
}

export interface WelcomeEmailData {
  recipientName: string;
  recipientEmail: string;
}

export async function sendWelcomeEmail(data: WelcomeEmailData): Promise<boolean> {
  try {
    const apiKey = import.meta.env.VITE_RESEND_API_KEY;

    if (!apiKey) {
      console.warn('Resend API key not configured, welcome email not sent');
      return false;
    }

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Welcome to Mentra</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f3f4f6;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f3f4f6; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" border="0" style="background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          <tr>
            <td style="background: linear-gradient(135deg, #8CAFA8 0%, #7A9B94 100%); padding: 50px 40px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 32px; font-weight: 700;">Welcome to Mentra!</h1>
              <p style="color: #E0EBE9; margin: 15px 0 0 0; font-size: 18px;">Your AI-powered career feedback platform</p>
            </td>
          </tr>
          <tr>
            <td style="padding: 40px;">
              <p style="color: #1f2937; font-size: 18px; line-height: 1.6; margin: 0 0 20px 0;">
                Hi ${data.recipientName},
              </p>
              <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                Thank you for joining Mentra! We're excited to help you accelerate your professional growth through AI-powered feedback insights.
              </p>
              <h2 style="color: #1f2937; font-size: 20px; margin: 30px 0 20px 0;">What you can do with Mentra:</h2>
              <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin: 0 0 30px 0;">
                <tr>
                  <td style="padding: 15px; background-color: #F0F4F3; border-left: 4px solid #8CAFA8; border-radius: 4px;">
                    <p style="color: #7A9B94; font-size: 15px; font-weight: 600; margin: 0 0 5px 0;">Request Feedback</p>
                    <p style="color: #4b5563; font-size: 14px; line-height: 1.5; margin: 0;">Easily request feedback from colleagues, managers, and team members</p>
                  </td>
                </tr>
              </table>
              <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin: 0 0 30px 0;">
                <tr>
                  <td style="padding: 15px; background-color: #F0F4F3; border-left: 4px solid #8CAFA8; border-radius: 4px;">
                    <p style="color: #7A9B94; font-size: 15px; font-weight: 600; margin: 0 0 5px 0;">AI-Powered Analysis</p>
                    <p style="color: #4b5563; font-size: 14px; line-height: 1.5; margin: 0;">Get intelligent insights and patterns from your feedback data</p>
                  </td>
                </tr>
              </table>
              <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin: 0 0 30px 0;">
                <tr>
                  <td style="padding: 15px; background-color: #F0F4F3; border-left: 4px solid #8CAFA8; border-radius: 4px;">
                    <p style="color: #7A9B94; font-size: 15px; font-weight: 600; margin: 0 0 5px 0;">Track Progress</p>
                    <p style="color: #4b5563; font-size: 14px; line-height: 1.5; margin: 0;">Monitor your growth over time with detailed analytics</p>
                  </td>
                </tr>
              </table>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  <td align="center" style="padding: 20px 0;">
                    <a href="${import.meta.env.VITE_APP_URL || 'https://getmentra.com'}/dashboard" style="display: inline-block; background-color: #8CAFA8; color: #ffffff; text-decoration: none; padding: 16px 40px; border-radius: 8px; font-weight: 600; font-size: 16px; box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);">
                      Go to Dashboard
                    </a>
                  </td>
                </tr>
              </table>
              <p style="color: #6b7280; font-size: 14px; line-height: 1.6; margin: 30px 0 0 0; text-align: center;">
                Need help getting started? Check out our quick start guide or reach out to our support team.
              </p>
            </td>
          </tr>
          <tr>
            <td style="background-color: #f9fafb; padding: 30px; text-align: center; border-top: 1px solid #e5e7eb;">
              <p style="color: #6b7280; font-size: 14px; margin: 0 0 10px 0;">
                Powered by <strong style="color: #8CAFA8;">Mentra</strong>
              </p>
              <p style="color: #9ca3af; font-size: 12px; margin: 0;">
                AI-powered career feedback platform
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `;

    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        from: 'Mentra <feedback@getmentra.com>',
        to: [data.recipientEmail],
        subject: 'Welcome to Mentra - Let\'s Get Started!',
        html: html
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Welcome email sending failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
      throw new Error(`Welcome email failed: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Welcome email sent successfully:', result);
    return true;
  } catch (error) {
    console.error('Error sending welcome email:', error);
    return false;
  }
}

export interface PasswordResetEmailData {
  recipientName: string;
  recipientEmail: string;
  resetLink: string;
}

export async function sendPasswordResetEmail(data: PasswordResetEmailData): Promise<boolean> {
  try {
    const apiKey = import.meta.env.VITE_RESEND_API_KEY;

    if (!apiKey) {
      console.warn('Resend API key not configured, password reset email not sent');
      return false;
    }

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Reset Your Password</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f3f4f6;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f3f4f6; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" border="0" style="background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          <tr>
            <td style="background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); padding: 40px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: 700;">Password Reset Request</h1>
              <p style="color: #fecaca; margin: 10px 0 0 0; font-size: 16px;">We received a request to reset your password</p>
            </td>
          </tr>
          <tr>
            <td style="padding: 40px;">
              <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                Hi ${data.recipientName},
              </p>
              <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                We received a request to reset the password for your Mentra account. Click the button below to choose a new password.
              </p>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  <td align="center" style="padding: 20px 0;">
                    <a href="${data.resetLink}" style="display: inline-block; background-color: #dc2626; color: #ffffff; text-decoration: none; padding: 16px 40px; border-radius: 8px; font-weight: 600; font-size: 16px; box-shadow: 0 4px 6px rgba(220, 38, 38, 0.3);">
                      Reset Your Password
                    </a>
                  </td>
                </tr>
              </table>
              <div style="background-color: #fef2f2; border-left: 4px solid #dc2626; padding: 20px; margin: 30px 0; border-radius: 4px;">
                <p style="color: #991b1b; font-size: 15px; font-weight: 600; margin: 0 0 10px 0;">
                  Important Security Information:
                </p>
                <ul style="color: #7f1d1d; font-size: 14px; line-height: 1.6; margin: 0; padding-left: 20px;">
                  <li>This link will expire in 1 hour</li>
                  <li>If you didn't request this reset, please ignore this email</li>
                  <li>Your password won't change until you access the link above</li>
                </ul>
              </div>
              <p style="color: #6b7280; font-size: 14px; line-height: 1.6; margin: 20px 0 0 0;">
                If you're having trouble clicking the button, copy and paste this URL into your browser:
              </p>
              <p style="color: #8CAFA8; font-size: 12px; word-break: break-all; margin: 10px 0 0 0;">
                ${data.resetLink}
              </p>
            </td>
          </tr>
          <tr>
            <td style="background-color: #f9fafb; padding: 30px; text-align: center; border-top: 1px solid #e5e7eb;">
              <p style="color: #6b7280; font-size: 14px; margin: 0 0 10px 0;">
                Powered by <strong style="color: #8CAFA8;">Mentra</strong>
              </p>
              <p style="color: #9ca3af; font-size: 12px; margin: 0;">
                AI-powered career feedback platform
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `;

    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        from: 'Mentra <feedback@getmentra.com>',
        to: [data.recipientEmail],
        subject: 'Reset Your Mentra Password',
        html: html
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Password reset email sending failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
      throw new Error(`Password reset email failed: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Password reset email sent successfully:', result);
    return true;
  } catch (error) {
    console.error('Error sending password reset email:', error);
    return false;
  }
}
